import { useCallback, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { ExportButton, type ExportFormat } from '@/components/atoms/ExportButton/ExportButton';
import { trackAnalyticsExport } from '@/modules/analytics/utils/analyticsTracking';
import { buildExportFilename } from '@/modules/analytics/utils/exportFilename';
import { PageName } from '@/shared/types/segment';
import { downloadCsv, downloadJson } from '@/util/fileManager';

import { useVoiceSessions, type VoiceSessionsData } from './hooks/useVoiceSessions';

interface MergedTimeseriesRow {
  time_bucket: string;
  total_voice_sessions: number | null;
  total_minutes: number | null;
  avg_session_length_seconds: number | null;
  voicemail_sessions: number | null;
  voicemail_rate: number | null;
  voicemail_duration_seconds: number | null;
  voicemail_minutes: number | null;
  voicemail_duration_count: number | null;
}

// Two decimals rather than a whole-minute round: sub-minute totals would
// otherwise export as a misleading 0.
const roundTwoDecimals = (value: number): number => Math.round(value * 100) / 100;

const mergeTimeseriesByDate = (data: VoiceSessionsData): MergedTimeseriesRow[] => {
  const byDate = new Map<string, MergedTimeseriesRow>();

  const ensure = (date: string): MergedTimeseriesRow => {
    const existing = byDate.get(date);
    if (existing) {
      return existing;
    }
    const row: MergedTimeseriesRow = {
      time_bucket: date,
      total_voice_sessions: null,
      total_minutes: null,
      avg_session_length_seconds: null,
      voicemail_sessions: null,
      voicemail_rate: null,
      voicemail_duration_seconds: null,
      voicemail_minutes: null,
      voicemail_duration_count: null,
    };
    byDate.set(date, row);
    return row;
  };

  for (const point of data.timeseries.totalSessions) {
    ensure(point.date).total_voice_sessions = point.value;
  }
  for (const point of data.timeseries.totalMinutes) {
    ensure(point.date).total_minutes = point.value;
  }
  for (const point of data.timeseries.avgSessionLength) {
    ensure(point.date).avg_session_length_seconds = point.value;
  }
  for (const point of data.timeseries.voicemailRate) {
    ensure(point.date).voicemail_rate = roundTwoDecimals(point.value);
  }
  for (const point of data.voicemailTimeseries) {
    const row = ensure(point.date);
    row.voicemail_sessions = point.sessions;
    row.voicemail_duration_seconds = point.durationSeconds;
    row.voicemail_minutes = roundTwoDecimals(point.durationSeconds / 60);
    row.voicemail_duration_count = point.durationCount;
  }

  return Array.from(byDate.values()).sort((a, b) => a.time_bucket.localeCompare(b.time_bucket));
};

const buildPayload = (data: VoiceSessionsData) => ({
  kpi: {
    total_voice_sessions: data.kpiValues.totalSessions.current,
    total_duration_seconds: data.totalDurationSeconds.current,
    total_minutes: roundTwoDecimals(data.kpiValues.totalMinutes.current),
    answered_duration_count: data.durationCount.current,
    avg_session_length_seconds:
      data.durationCount.current > 0 ? data.kpiValues.avgSessionLength.current : null,
    voicemail_sessions: data.voicemailSessions.current,
    voicemail_rate: roundTwoDecimals(data.kpiValues.voicemailRate.current),
    voicemail_duration_seconds: data.voicemailDurationSeconds.current,
    voicemail_minutes: roundTwoDecimals(data.voicemailMinutes.current),
    voicemail_duration_count: data.voicemailDurationCount.current,
  },
  timeseries: mergeTimeseriesByDate(data),
});

/**
 * Per-widget icon export button for the Voice Sessions section.
 *
 * Mirrors the AI Agents Insights export: dumps the KPI tile values and the
 * per-bucket timeseries data shown in the chart below. Totals include voicemail,
 * like the tiles; the average covers answered calls only.
 */
export const VoiceSessionsExport = () => {
  const { t } = useTranslation();
  const { data } = useVoiceSessions();
  const [isExporting, setIsExporting] = useState(false);

  const handleExport = useCallback(
    (format: ExportFormat) => {
      try {
        setIsExporting(true);
        trackAnalyticsExport(PageName.ANALYTICS_AI_AGENTS, 'voice_sessions', format);

        const payload = buildPayload(data);
        const filename = buildExportFilename('voice_sessions');

        if (format === 'json') {
          downloadJson(payload, filename);
          return;
        }

        const rows: Record<string, unknown>[] = [
          { row_type: 'kpi', ...payload.kpi },
          ...payload.timeseries.map((row) => ({ row_type: 'timeseries', ...row })),
        ];
        downloadCsv(rows, filename);
      } catch {
        toast.error(t('analytics.export_error'));
      } finally {
        setIsExporting(false);
      }
    },
    [data, t]
  );

  return <ExportButton variant="icon" onExport={handleExport} isExporting={isExporting} />;
};
